import { InstallSource } from "./install-source";

export const FIRST_INSTALL_ONBOARDING_VERSION = 1;

export const FIRST_INSTALL_ONBOARDING_SETUP_SOURCE = "tc_accept";

export const FIRST_INSTALL_ATTRIBUTION_SOURCES = [
  "search",
  "shopify_app_store",
  "linkedin",
  "youtube",
  "social",
  "podcast",
  "article",
  "referral",
  "event",
  "existing_redo",
  "other",
] as const;

export type FirstInstallAttributionSource =
  (typeof FIRST_INSTALL_ATTRIBUTION_SOURCES)[number];
export const FirstInstallAttributionSource = FIRST_INSTALL_ATTRIBUTION_SOURCES;

export const FIRST_INSTALL_ATTRIBUTION_SOURCE_LABELS = {
  search: "Search engine",
  shopify_app_store: "Shopify app store",
  linkedin: "LinkedIn",
  youtube: "YouTube",
  social: "Instagram, X, or TikTok",
  podcast: "Podcast",
  article: "Article or blog post",
  referral: "Friend or colleague",
  event: "Event or conference",
  existing_redo: "Redo Team Member",
  other: "Somewhere else",
} as const satisfies Record<FirstInstallAttributionSource, string>;

export const FIRST_INSTALL_ONBOARDING_PRODUCTS = [
  "returns",
  "withdrawals",
  "order-editing",
  "shipping-fulfillment",
  "chargebacks",
  "recover",
  "contextual",
  "email-sms",
  "aeo",
  "checkout-optimization",
  "ai-sales-support",
  "inventory-management",
] as const;

export type FirstInstallOnboardingProduct =
  (typeof FIRST_INSTALL_ONBOARDING_PRODUCTS)[number];
export const FirstInstallOnboardingProduct = FIRST_INSTALL_ONBOARDING_PRODUCTS;

export const FIRST_INSTALL_ONBOARDING_PRODUCT_LABELS = {
  returns: "Returns",
  withdrawals: "Withdrawals",
  "order-editing": "Order Editing",
  "shipping-fulfillment": "Shipping & Fulfillment",
  chargebacks: "Chargebacks",
  recover: "Recover",
  contextual: "Contextual",
  "email-sms": "Email & SMS",
  aeo: "Answer Engine Optimization",
  "checkout-optimization": "Checkout Optimization",
  "ai-sales-support": "AI Sales + Support",
  "inventory-management": "Inventory Management",
} as const satisfies Record<FirstInstallOnboardingProduct, string>;

export interface FirstInstallOnboardingState {
  version: typeof FIRST_INSTALL_ONBOARDING_VERSION;
  completedAt?: string | null;
  attributionSource?: FirstInstallAttributionSource | null;
  selectedProduct?: FirstInstallOnboardingProduct | null;
}

export interface MerchantAppOnboardingDestination {
  type: "merchant";
  path: string;
}

export interface SetupAppOnboardingDestination {
  type: "onboarding";
  redirectUrl: string;
}

export type FirstInstallOnboardingDestination =
  | MerchantAppOnboardingDestination
  | SetupAppOnboardingDestination;

export interface CompleteFirstInstallOnboardingRequest {
  attributionSource: FirstInstallAttributionSource;
  selectedProduct?: FirstInstallOnboardingProduct;
}

export interface DismissFirstInstallOnboardingRequest {
  attributionSource?: FirstInstallAttributionSource;
}

export interface CompleteFirstInstallOnboardingResponse {
  destination: FirstInstallOnboardingDestination;
}

export interface AcceptTermsAndConditionsRequest {
  firstInstallOnboardingVersion?: typeof FIRST_INSTALL_ONBOARDING_VERSION;
}

export function shouldAskForFirstInstallProduct(
  installSource: InstallSource | null | undefined,
): boolean {
  return (
    installSource === InstallSource.MAIN_APP ||
    typeof installSource === "undefined" ||
    installSource === null
  );
}

export function resolveFirstInstallOnboardingProduct(args: {
  installSource: InstallSource | null | undefined;
  selectedProduct?: FirstInstallOnboardingProduct;
}): FirstInstallOnboardingProduct | null {
  if (shouldAskForFirstInstallProduct(args.installSource)) {
    return args.selectedProduct ?? null;
  }
  switch (args.installSource) {
    case InstallSource.RECLAIM_APP:
      return "chargebacks";
    case InstallSource.ORDER_TRACKING_APP_BUT_ACTUALLY_RECOVER:
      return "recover";
  }
  return null;
}

export function getFirstInstallMerchantOnboardingDestination(args: {
  product: FirstInstallOnboardingProduct;
  teamId: string;
  startSetupGuide: boolean;
}): MerchantAppOnboardingDestination | null {
  switch (args.product) {
    case "chargebacks": {
      const setupSourceParam = args.startSetupGuide
        ? `?setupSource=${FIRST_INSTALL_ONBOARDING_SETUP_SOURCE}`
        : "";
      return {
        type: "merchant",
        path: `/stores/${args.teamId}/payments/chargebacks/overview${setupSourceParam}`,
      };
    }
    case "withdrawals":
      return {
        type: "merchant",
        path: `/stores/${args.teamId}/returns/withdrawals/all-withdrawals?setup=true`,
      };
    case "order-editing":
      return {
        type: "merchant",
        path: `/stores/${args.teamId}/welcome/order-editing`,
      };
    case "recover":
      return {
        type: "merchant",
        path: `/stores/${args.teamId}/welcome/unified-onboarding?product=recover`,
      };
    default:
      return null;
  }
}

/**
 * Returned from PUT /onboarding/tc. Legacy callers navigate to redirectUrl
 * immediately. The v2 first-install modal calls the same endpoint after
 * attribution/product selection, then completes the first-install flow before
 * redirecting.
 */
export interface TcAcceptResponse {
  redirectUrl: string;
}

export interface Onboarding {
  finished: boolean;
  processing: boolean;
  confirmed: boolean;
  tcConfirmed: boolean;
  firstInstallOnboarding?: FirstInstallOnboardingState | null;
  _id: string;
  team: string;
  updatedAt: string;
  credit: { value: string };
  labelPrice: string;
  orders: {
    fromDate: string;
    toDate: string;
    count: number;
    countRefunded: number;
    refundPercentage: number;
    realRefundPercentage: number;
  };
}
